import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import FileItem from './file_item';

const ROW_HEIGHT = 50;
const OVERSCAN_ROWS = 12;
const COLUMN_COUNT = 6;

const FileList = ({ 
  files, 
  currentPath, 
  selectedItems, 
  selectedItemPaths,
  onSelectItem, 
  onItemClick, 
  onDoubleClick, 
  paneId, 
  onDropOnItem, 
  onContextMenu, 
  sortConfig, 
  onSort,
  renamingItem,
  focusedIndex,
  isVisible,
  onRenameCancel,
  onRenameSubmit
}) => {
  const scrollRef = useRef(null);
  const [viewport, setViewport] = useState({ scrollTop: 0, height: 0 });

  const isSelected = (item) => selectedItemPaths.has(item.path);

  const updateViewport = useCallback(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) return;

    const nextViewport = {
      scrollTop: scrollElement.scrollTop,
      height: scrollElement.clientHeight,
    };

    setViewport(prev => (
      prev.scrollTop === nextViewport.scrollTop && prev.height === nextViewport.height
        ? prev
        : nextViewport
    ));
  }, []);

  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) return undefined;

    updateViewport();
    scrollElement.addEventListener('scroll', updateViewport, { passive: true });
    window.addEventListener('resize', updateViewport);
    let resizeObserver;

    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(updateViewport);
      resizeObserver.observe(scrollElement);
    }

    return () => {
      scrollElement.removeEventListener('scroll', updateViewport);
      window.removeEventListener('resize', updateViewport);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [files.length, updateViewport]);

  useLayoutEffect(() => {
    if (!isVisible) return undefined;

    updateViewport();
    const frame = window.requestAnimationFrame(updateViewport);

    return () => window.cancelAnimationFrame(frame);
  }, [files.length, isVisible, updateViewport]);

  const virtualRows = useMemo(() => {
    const start = Math.max(0, Math.floor(viewport.scrollTop / ROW_HEIGHT) - OVERSCAN_ROWS);
    const visibleRowCount = Math.ceil(viewport.height / ROW_HEIGHT) + (OVERSCAN_ROWS * 2);
    const end = Math.min(files.length, start + visibleRowCount);

    return {
      start,
      end,
      topHeight: start * ROW_HEIGHT,
      bottomHeight: Math.max(0, (files.length - end) * ROW_HEIGHT),
      rows: files.slice(start, end),
    };
  }, [files, viewport]);

  useEffect(() => {
    if (focusedIndex == null || focusedIndex < 0) return;

    const scrollElement = scrollRef.current;
    if (!scrollElement) return;

    const rowTop = focusedIndex * ROW_HEIGHT;
    const rowBottom = rowTop + ROW_HEIGHT;
    const viewportTop = scrollElement.scrollTop;
    const viewportBottom = viewportTop + scrollElement.clientHeight;

    if (rowTop < viewportTop) {
      scrollElement.scrollTop = rowTop;
    } else if (rowBottom > viewportBottom) {
      scrollElement.scrollTop = rowBottom - scrollElement.clientHeight;
    }
  }, [focusedIndex]);

  const renderSortIcon = (key) => {
    if (sortConfig?.key !== key) return null;
    return <i className={`fa fa-sort-${sortConfig.direction === 'asc' ? 'asc' : 'desc'} ml-1`} />;
  };

  if (files.length === 0) {
    return (
      <div
        ref={scrollRef}
        className="file-list empty"
        onContextMenu={(e) => onContextMenu(e, null)}
      >
        <div className="text-center text-muted p-5">
          <i className="fa fa-folder-open fa-3x mb-3" />
          <p>Dieser Ordner ist leer</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={scrollRef}
      className="file-list"
      onContextMenu={(e) => {
        // Only trigger if clicking directly on the container or table parts (not rows which handle it themselves)
        if (e.target === e.currentTarget || e.target.closest('thead')) {
           onContextMenu(e, null);
        }
      }}
    >
      <table className="table table-hover table-sm">
        <thead>
          <tr>
            <th style={{ width: '30px' }}></th>
            <th style={{ width: '30px' }}></th>
            <th onClick={() => onSort('name')} style={{ cursor: 'pointer' }}>
              Name {renderSortIcon('name')}
            </th>
            <th style={{ width: '120px', cursor: 'pointer' }} onClick={() => onSort('size')}>
              Größe {renderSortIcon('size')}
            </th>
            <th style={{ width: '100px', cursor: 'pointer' }} onClick={() => onSort('mode')}>
              Berechtigungen {renderSortIcon('mode')}
            </th>
            <th style={{ width: '180px', cursor: 'pointer' }} onClick={() => onSort('mtime')}>
              Geändert {renderSortIcon('mtime')}
            </th>
          </tr>
        </thead>
        <tbody>
          {virtualRows.topHeight > 0 && (
            <tr className="file-list-spacer" aria-hidden="true">
              <td colSpan={COLUMN_COUNT} style={{ height: `${virtualRows.topHeight}px`, padding: 0, border: 0 }} />
            </tr>
          )}

          {virtualRows.rows.map((file, offset) => {
            const index = virtualRows.start + offset;
            return (
              <FileItem
                key={file.path}
                item={file}
                itemIndex={index}
                isSelected={isSelected(file)}
                onSelect={(selected) => onSelectItem(file, selected, index)}
                onItemClick={(e) => onItemClick(file, index, e)}
                onDoubleClick={() => onDoubleClick(file)}
                onContextMenu={(e) => onContextMenu(e, file)}
                paneId={paneId}
                currentPath={currentPath}
                selectedItems={selectedItems}
                onDropOnItem={onDropOnItem}
                isRenaming={renamingItem?.path === file.path}
                onRenameCancel={onRenameCancel}
                onRenameSubmit={onRenameSubmit}
              />
            );
          })}

          {virtualRows.bottomHeight > 0 && (
            <tr className="file-list-spacer" aria-hidden="true">
              <td colSpan={COLUMN_COUNT} style={{ height: `${virtualRows.bottomHeight}px`, padding: 0, border: 0 }} />
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

FileList.propTypes = {
  files: PropTypes.array.isRequired,
  currentPath: PropTypes.string.isRequired,
  selectedItems: PropTypes.array.isRequired,
  selectedItemPaths: PropTypes.instanceOf(Set).isRequired,
  onSelectItem: PropTypes.func.isRequired,
  onItemClick: PropTypes.func.isRequired,
  onDoubleClick: PropTypes.func.isRequired,
  paneId: PropTypes.string.isRequired,
  onDropOnItem: PropTypes.func,
  onContextMenu: PropTypes.func.isRequired,
  sortConfig: PropTypes.object,
  onSort: PropTypes.func,
  renamingItem: PropTypes.object,
  focusedIndex: PropTypes.number,
  isVisible: PropTypes.bool,
  onRenameCancel: PropTypes.func,
  onRenameSubmit: PropTypes.func,
};

FileList.defaultProps = {
  isVisible: true,
};

export default FileList;
